'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, AlertTitle, Box } from '@mui/material';
import { Buttons, Layout } from '../material';
import { TextLarge, TextRegular } from '../text-components';
import { ErrorDiagnosticInfo } from './ErrorDiagnosticInfo';
import type { ErrorDiagnosticInfoProps } from './ErrorDiagnosticInfo';
export type UserFriendlyErrorProps = {
  /**
   * User-friendly error title
   */
  title?: string;

  /**
   * User-friendly error message
   */
  message: string;

  /**
   * Optional explanation of *why* the error happened (e.g. why the user is
   * unauthorized). Rendered as an informational note below the message.
   */
  details?: React.ReactNode;

  /**
   * Optional severity for the primary alert.
   * @default 'error'
   */
  severity?: 'error' | 'warning' | 'info';

  /**
   * Optional label for the primary action button (e.g. "Sign in again").
   * @default 'Try again'
   */
  retryLabel?: string;

  /**
   * Optional short title for the message card.
   */
  alertTitle?: string;

  /**
   * Optional error reference ID for support
   */
  errorId?: string;

  /**
   * Optional retry callback
   */
  onRetry?: () => void;

  /**
   * Holds the retry button in a disabled, spinning state while a retry that
   * cannot resolve in-place (e.g. a chunk purge + reload) is still running.
   */
  retryPending?: boolean;

  /**
   * Optional home navigation callback
   */
  onGoHome?: () => void;

  /**
   * Optional collapsible diagnostics panel shown above action buttons.
   */
  diagnosticInfo?: ErrorDiagnosticInfoProps;
};

/**
 * User-friendly error display component
 *
 * Displays errors in a polished, consistent way with:
 * - Clear, non-technical messaging
 * - Error reference ID for support
 * - Optional diagnostic panel for support correlation
 * - Actionable buttons (retry, go home)
 */
export function UserFriendlyError(t0) {
  const $ = _c(40);
  const {
    title: t1,
    message,
    details,
    severity: t2,
    retryLabel: t3,
    alertTitle: t4,
    errorId,
    onRetry,
    retryPending: t5,
    onGoHome,
    diagnosticInfo
  } = t0;
  const title = t1 === undefined ? "Something went wrong" : t1;
  const severity = t2 === undefined ? "error" : t2;
  const retryLabel = t3 === undefined ? "Try again" : t3;
  const alertTitle = t4 === undefined ? "What happened" : t4;
  const retryPending = t5 === undefined ? false : t5;
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      gap: 3,
      maxWidth: 600,
      margin: "0 auto",
      width: "100%"
    };
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  let t7;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      gap: 2,
      alignItems: "center"
    };
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const t8 = `${severity}.main`;
  let t9;
  if ($[2] !== t8) {
    t9 = <ErrorOutlineIcon sx={{
      fontSize: 48,
      color: t8
    }} />;
    $[2] = t8;
    $[3] = t9;
  } else {
    t9 = $[3];
  }
  let t10;
  if ($[4] !== title) {
    t10 = <TextLarge variant="title" weight="bold">{title}</TextLarge>;
    $[4] = title;
    $[5] = t10;
  } else {
    t10 = $[5];
  }
  let t11;
  if ($[6] !== t10 || $[7] !== t9) {
    t11 = <Layout.BoxHorizontal sx={t7}>{t9}{t10}</Layout.BoxHorizontal>;
    $[6] = t10;
    $[7] = t9;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  let t12;
  if ($[9] !== alertTitle) {
    t12 = <AlertTitle>{alertTitle}</AlertTitle>;
    $[9] = alertTitle;
    $[10] = t12;
  } else {
    t12 = $[10];
  }
  let t13;
  if ($[11] !== message) {
    t13 = <TextRegular>{message}</TextRegular>;
    $[11] = message;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  let t14;
  if ($[13] !== severity || $[14] !== t12 || $[15] !== t13) {
    t14 = <Alert severity={severity}>{t12}{t13}</Alert>;
    $[13] = severity;
    $[14] = t12;
    $[15] = t13;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== details) {
    t15 = details && <Alert severity="info" icon={<InfoOutlinedIcon />}><AlertTitle>Why am I seeing this?</AlertTitle>{typeof details === "string" ? <TextRegular>{details}</TextRegular> : details}</Alert>;
    $[17] = details;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  let t16;
  if ($[19] !== errorId) {
    t16 = errorId && <Box sx={{
      p: 2,
      bgcolor: "grey.100",
      borderRadius: 1
    }}><TextRegular variant="muted">Support Reference ID: <code>{errorId}</code></TextRegular><Layout.Spacer /><TextRegular variant="muted">Share this ID with support so we can find the error details.</TextRegular></Box>;
    $[19] = errorId;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] !== diagnosticInfo) {
    t17 = diagnosticInfo && <ErrorDiagnosticInfo {...diagnosticInfo} />;
    $[21] = diagnosticInfo;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      gap: 2,
      justifyContent: "center"
    };
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== onRetry || $[25] !== retryLabel || $[26] !== retryPending) {
    t19 = onRetry && <Buttons.PrimaryButton onClick={onRetry} text={retryLabel} loading={retryPending} />;
    $[24] = onRetry;
    $[25] = retryLabel;
    $[26] = retryPending;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  if ($[28] !== onGoHome) {
    t20 = onGoHome && <Buttons.SecondaryButton onClick={onGoHome} text="Go home" />;
    $[28] = onGoHome;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] !== t19 || $[31] !== t20) {
    t21 = <Layout.BoxHorizontal sx={t18}>{t19}{t20}</Layout.BoxHorizontal>;
    $[30] = t19;
    $[31] = t20;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== t11 || $[34] !== t14 || $[35] !== t15 || $[36] !== t16 || $[37] !== t17 || $[38] !== t21) {
    t22 = <Layout.BoxVertical sx={t6}>{t11}{t14}{t15}{t16}{t17}{t21}</Layout.BoxVertical>;
    $[33] = t11;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
    $[38] = t21;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  return t22;
}